// frontend/src/components/MobileFooterNav.js
import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useCartStatus } from "../hooks/useCart";
import useSidebarConfig from "../hooks/useSidebarConfig";
import { FiHome, FiShoppingCart, FiCheckSquare, FiBarChart2, FiFile, FiUser } from "react-icons/fi";
import routeRegistry from "../services/routeRegistry";
import { useTranslation } from "../i18n";
import {
  MAX_MOBILE_NAV_ROUTES,
  resolveMobileNavRoutes,
  routeIconForPath,
  routeLabels,
} from "../constants/routes";
import { ICON_MAP } from "./sidebar/DashboardSidebar";

const HOME_PATH = '/products';

const SHOP_BUTTONS = {
  [HOME_PATH]: { icon: FiHome, label: 'Home' },
  '/cart': { icon: FiShoppingCart, label: 'Cart' },
  '/checkout': { icon: FiCheckSquare, label: 'Checkout' },
  '/dashboard': { icon: FiBarChart2, label: 'Dashboard' },
};

const linkPath = (path) => (path === '/' ? HOME_PATH : path);
const permissionPath = (path) => (path === HOME_PATH ? '/' : path);

const MobileFooterNav = () => {
  const { t } = useTranslation();
  const { user, userRoles, isSuperuser } = useContext(AuthContext);
  const { configuration } = useSidebarConfig();
  const location = useLocation();
  // Use new cart hook to get cart data
  const { itemCount: totalItems, totalPrice } = useCartStatus();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Check route permissions
  const userRoleNames = Array.isArray(userRoles) ? userRoles : [];
  const effectiveRoles = isSuperuser ? [...userRoleNames, 'admin'] : userRoleNames;

  const paths = user
    ? [...new Set(resolveMobileNavRoutes(configuration?.mobile_nav_routes).map(linkPath))]
        .filter((path) => routeRegistry.hasAccess(permissionPath(path), effectiveRoles))
        .slice(0, MAX_MOBILE_NAV_ROUTES)
    : [HOME_PATH];

  const renderButton = (path) => {
    const shopButton = SHOP_BUTTONS[path];
    const Icon = ICON_MAP[configuration?.route_icons?.[path]] || shopButton?.icon || routeIconForPath(path) || FiFile;
    const label = configuration?.route_labels?.[path] || shopButton?.label || routeLabels[path] || path;
    const active = isActive(path) || (path === HOME_PATH && isActive('/'));
    const isCart = path === '/cart';

    return (
      <div className="text-center" key={path}>
        <Link
          to={path}
          className={`flex flex-col items-center ${active ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <div className="relative">
            <Icon className="text-xl mb-1" />
            {isCart && totalItems > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs bg-red-600 text-white rounded-full">
                {totalItems > 9 ? '9+' : totalItems}
              </span>
            )}
          </div>
          <span className="text-xs">{isCart && totalItems > 0 ? `£${totalPrice.toFixed(2)}` : t(label)}</span>
        </Link>
      </div>
    );
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 py-2 z-50" style={{ height: '60px' }}>
      <div className="flex justify-around items-center h-full">
        {paths.map(renderButton)}

        {!user && (
          <div className="text-center">
            <Link
              to="/login"
              className={`flex flex-col items-center ${isActive('/login') ? 'text-primary' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <FiUser className="text-xl mb-1" />
              <span className="text-xs">{t("Login")}</span>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default MobileFooterNav;